import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { makeRequest } from "../../utils/request";

export interface PendingFunctionApproval {
  id: string;
  agentId: string;
  agentEnvironmentId: string;
  optimizerId: string;
  functionId: string;
  testName: string | null;
  createdAt: string;
}

export interface PendingFunctionApprovalsState {
  approvals: Record<string, PendingFunctionApproval[]>; // agentId -> approvals[]
  isLoading: Record<string, boolean>;
  error: Record<string, string | null>;
}

const initialState: PendingFunctionApprovalsState = {
  approvals: {},
  isLoading: {},
  error: {},
};

export const fetchPendingApprovals = createAsyncThunk("pendingApprovals/fetchApprovals", async (agentId: string) => {
  const response = await makeRequest<{ data: PendingFunctionApproval[] }>(`/v1/pending-function-approvals/agent/${agentId}`);
  return { agentId, approvals: response.data || [] };
});

export const approveFunction = createAsyncThunk(
  "pendingApprovals/approveFunction",
  async ({ approvalId, agentId }: { approvalId: string; agentId: string }) => {
    await makeRequest(`/v1/pending-function-approvals/${approvalId}/approve`, {
      method: "POST",
    });
    return { approvalId, agentId };
  },
);

export const saveFunction = createAsyncThunk("pendingApprovals/saveFunction", async ({ approvalId, agentId }: { approvalId: string; agentId: string }) => {
  await makeRequest(`/v1/pending-function-approvals/${approvalId}/save`, {
    method: "POST",
  });
  return { approvalId, agentId };
});

export const deleteFunction = createAsyncThunk("pendingApprovals/deleteFunction", async ({ approvalId, agentId }: { approvalId: string; agentId: string }) => {
  const response = await makeRequest<{ success: boolean; previousFunctionId?: string }>(`/v1/pending-function-approvals/${approvalId}/delete`, {
    method: "POST",
  });
  return { approvalId, agentId, previousFunctionId: response.previousFunctionId };
});

const pendingApprovalsSlice = createSlice({
  name: "pendingApprovals",
  initialState,
  reducers: {
    clearApprovals: (state, action: PayloadAction<string>) => {
      const agentId = action.payload;
      delete state.approvals[agentId];
      delete state.isLoading[agentId];
      delete state.error[agentId];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingApprovals.pending, (state, action) => {
        const agentId = action.meta.arg;
        state.isLoading[agentId] = true;
        state.error[agentId] = null;
      })
      .addCase(fetchPendingApprovals.fulfilled, (state, action) => {
        const { agentId, approvals } = action.payload;
        state.approvals[agentId] = approvals;
        state.isLoading[agentId] = false;
        state.error[agentId] = null;
      })
      .addCase(fetchPendingApprovals.rejected, (state, action) => {
        const agentId = action.meta.arg;
        state.isLoading[agentId] = false;
        state.error[agentId] = action.error.message || "Failed to fetch pending approvals";
      })
      .addCase(approveFunction.fulfilled, (state, action) => {
        const { approvalId, agentId } = action.payload;
        if (state.approvals[agentId]) {
          state.approvals[agentId] = state.approvals[agentId].filter((approval) => approval.id !== approvalId);
        }
      })
      .addCase(approveFunction.rejected, (state, action) => {
        // If approval not found, refresh the approvals list to get current state
        const agentId = action.meta.arg.agentId;
        if (action.error.message?.includes("Item not found")) {
          // Clear the approvals for this agent to force a refresh
          delete state.approvals[agentId];
        }
      })
      .addCase(saveFunction.fulfilled, (state, action) => {
        const { approvalId, agentId } = action.payload;
        if (state.approvals[agentId]) {
          state.approvals[agentId] = state.approvals[agentId].filter((approval) => approval.id !== approvalId);
        }
      })
      .addCase(saveFunction.rejected, (state, action) => {
        // If approval not found, refresh the approvals list to get current state
        const agentId = action.meta.arg.agentId;
        if (action.error.message?.includes("Item not found")) {
          // Clear the approvals for this agent to force a refresh
          delete state.approvals[agentId];
        }
      })
      .addCase(deleteFunction.fulfilled, (state, action) => {
        const { approvalId, agentId } = action.payload;
        if (state.approvals[agentId]) {
          state.approvals[agentId] = state.approvals[agentId].filter((approval) => approval.id !== approvalId);
        }
      })
      .addCase(deleteFunction.rejected, (state, action) => {
        // If approval not found, refresh the approvals list to get current state
        const agentId = action.meta.arg.agentId;
        if (action.error.message?.includes("Item not found")) {
          // Clear the approvals for this agent to force a refresh
          delete state.approvals[agentId];
        }
      });
  },
});

export const { clearApprovals } = pendingApprovalsSlice.actions;
export default pendingApprovalsSlice.reducer;
